import axios, { type AxiosInstance, type AxiosResponse } from "axios"

class ApiService {
  private api: AxiosInstance
  private isRefreshing = false
  private refreshFailed = false // Global flag to prevent refresh attempts after failure
  private isRedirecting = false // Flag to prevent multiple redirects
  private lastRefreshAttempt = 0 // Timestamp of last refresh attempt (for cooldown)
  private refreshInterval: ReturnType<typeof setInterval> | null = null // Interval for proactive token refresh
  // Type definition for queue - parameters are part of function signature, not actual usage
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private failedQueue: Array<{
    // @ts-ignore
    resolve: (value?: any) => void
    // @ts-ignore
    reject: (reason?: any) => void
  }> = []

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || "http://localhost:3000",
      timeout: 10000,
      withCredentials: true, // Enable cookies
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      config => {
        // Tokens are now in HTTP-only cookies, no need to add Authorization header
        // Add header to identify backoffice requests
        config.headers = config.headers || {}
        config.headers["X-Application"] = "backoffice"
        return config
      },
      error => {
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle errors and token refresh
    this.api.interceptors.response.use(
      response => response,
      async (error: unknown) => {
        const axiosError = error as {
          response?: { status?: number }
          config?: Record<string, unknown>
        }
        const originalRequest = axiosError.config as
          | { _retry?: boolean; _skipRefresh?: boolean; url?: string }
          | undefined

        // Handle 403 Forbidden - anonymous users not allowed in backoffice
        if (axiosError.response?.status === 403) {
          console.warn("🚫 [ApiService] 403 Forbidden - anonymous users not allowed", {
            url: originalRequest?.url,
          })
          if (originalRequest) {
            originalRequest._skipRefresh = true
          }

          // Clear auth state
          if (typeof window !== "undefined") {
            try {
              console.log("🚪 [ApiService] Calling /auth/logout to clear cookies")
              await axios.post(
                `${import.meta.env.VITE_API_URL || "http://localhost:3000"}/auth/logout`,
                {},
                { withCredentials: true }
              )
              console.log("✅ [ApiService] Logout successful")
            } catch (logoutError) {
              console.error("❌ [ApiService] Logout failed (ignoring):", logoutError)
            }

            if (typeof globalThis !== "undefined" && "location" in globalThis) {
              const currentPath = globalThis.location?.pathname || ""
              if (!currentPath.includes("/login")) {
                console.log("🚪 [ApiService] Redirecting to /login (403 Forbidden)")
                ;(globalThis as { location: { href: string } }).location.href = "/login"
              } else {
                console.log("🚪 [ApiService] Already on login page, skipping redirect (403)")
              }
            }
          }

          return Promise.reject(error)
        }

        // CRITICAL: If refresh already failed, reject ALL 401 requests immediately
        // This prevents infinite loops when there are no tokens
        if (axiosError.response?.status === 401 && this.refreshFailed) {
          console.warn("⚠️ [ApiService] Refresh already failed, rejecting request immediately", {
            url: originalRequest?.url,
          })

          // Mark request to skip refresh to prevent retry
          if (originalRequest) {
            originalRequest._skipRefresh = true
          }

          // Redirect to login (only once and not if already on login page)
          if (
            !this.isRedirecting &&
            typeof globalThis !== "undefined" &&
            "location" in globalThis
          ) {
            const currentPath = globalThis.location?.pathname || ""
            if (!currentPath.includes("/login")) {
              this.isRedirecting = true
              console.log("🚪 [ApiService] Redirecting to /login (refresh already failed)")
              setTimeout(() => {
                ;(globalThis as { location: { href: string } }).location.href = "/login"
              }, 100)
            } else {
              console.log("🚪 [ApiService] Already on login page, skipping redirect")
            }
          }

          return Promise.reject(error)
        }

        // Handle 401 Unauthorized - try to refresh token
        // ONLY if refresh hasn't failed yet and request is not excluded
        if (
          axiosError.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !originalRequest._skipRefresh &&
          !this.refreshFailed && // CRITICAL: Don't try refresh if it already failed
          !originalRequest.url?.includes("/auth/refresh") &&
          !originalRequest.url?.includes("/auth/generate-token") &&
          !originalRequest.url?.includes("/auth/logout")
        ) {
          // If already refreshing, queue this request
          if (this.isRefreshing) {
            console.log("⏳ [ApiService] Refresh already in progress, queueing request", {
              url: originalRequest.url,
              queueSize: this.failedQueue.length + 1,
            })
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject })
            })
              .then(() => {
                // Only retry if refresh succeeded
                if (!this.refreshFailed) {
                  console.log("✅ [ApiService] Retrying queued request:", originalRequest.url)
                  return this.api(originalRequest as Parameters<typeof this.api>[0])
                }
                console.log(
                  "❌ [ApiService] Refresh failed, rejecting queued request:",
                  originalRequest.url
                )
                return Promise.reject(new Error("Refresh failed"))
              })
              .catch(err => {
                return Promise.reject(err)
              })
          }

          // Check cooldown before attempting refresh (min. 1 second)
          const now = Date.now()
          const timeSinceLastAttempt = now - this.lastRefreshAttempt
          const REFRESH_COOLDOWN_MS = 1000 // 1 second

          if (timeSinceLastAttempt < REFRESH_COOLDOWN_MS) {
            console.log("⏳ [ApiService] Refresh cooldown active, skipping...", {
              timeSinceLastAttempt,
              cooldown: REFRESH_COOLDOWN_MS,
            })
            return Promise.reject(error)
          }

          // Start refresh attempt
          originalRequest._retry = true
          this.isRefreshing = true
          this.lastRefreshAttempt = now

          console.log("🔄 [ApiService] Starting token refresh due to 401 error", {
            url: originalRequest.url,
            queueSize: this.failedQueue.length,
          })

          try {
            // Try to refresh token using refresh_token cookie
            console.log("🔄 [ApiService] Calling /auth/refresh endpoint...")

            const refreshResponse = await axios.post(
              `${import.meta.env.VITE_API_URL || "http://localhost:3000"}/auth/refresh`,
              {},
              {
                withCredentials: true, // Send refresh_token cookie
                headers: {
                  "X-Application": "backoffice",
                },
              }
            )

            // Check if refresh was successful (status 200)
            if (refreshResponse.status === 200) {
              console.log("✅ [ApiService] Token refresh successful")

              // Refresh succeeded - reset flag
              this.refreshFailed = false
              this.isRedirecting = false

              // Restart proactive refresh interval
              this.startProactiveRefresh()

              // Notify WebSocket service about token refresh
              this.notifyWebSocketRefresh()

              // Process queued requests
              console.log(`📦 [ApiService] Processing ${this.failedQueue.length} queued requests`)
              this.failedQueue.forEach(prom => prom.resolve())
              this.failedQueue = []

              // Retry original request
              console.log("🔄 [ApiService] Retrying original request:", originalRequest.url)
              return this.api(originalRequest as Parameters<typeof this.api>[0])
            } else {
              // Unexpected response status
              throw new Error(`Unexpected refresh response status: ${refreshResponse.status}`)
            }
          } catch (refreshError: unknown) {
            const error = refreshError as { response?: { status?: number }; message?: string }
            console.error("❌ [ApiService] Token refresh failed:", {
              status: error?.response?.status,
              message: error?.message,
            })

            // CRITICAL: Set global flag to prevent further refresh attempts
            // This is the key to preventing infinite loops
            this.refreshFailed = true

            // Clear queue immediately
            console.log(`❌ [ApiService] Rejecting ${this.failedQueue.length} queued requests`)
            this.failedQueue.forEach(prom => prom.reject(refreshError))
            this.failedQueue = []

            // Mark that refresh failed to prevent infinite loop
            if (originalRequest) {
              originalRequest._skipRefresh = true
            }

            // Redirect to login page (only once and not if already on login page)
            if (
              !this.isRedirecting &&
              typeof globalThis !== "undefined" &&
              "location" in globalThis
            ) {
              const currentPath = globalThis.location?.pathname || ""
              if (!currentPath.includes("/login")) {
                this.isRedirecting = true
                console.log("🚪 [ApiService] Redirecting to /login due to refresh failure")
                setTimeout(() => {
                  ;(globalThis as { location: { href: string } }).location.href = "/login"
                }, 100)
              } else {
                console.log("🚪 [ApiService] Already on login page, skipping redirect")
              }
            }

            return Promise.reject(refreshError)
          } finally {
            this.isRefreshing = false
            console.log(
              "🏁 [ApiService] Token refresh process finished, isRefreshing:",
              this.isRefreshing
            )
          }
        }

        // All other errors - reject normally
        return Promise.reject(error)
      }
    )
  }

  // Reset refresh failed flag (call after successful login)
  resetRefreshFailed() {
    console.log("🔄 [ApiService] Resetting refresh failed flag and starting proactive refresh")
    this.refreshFailed = false
    this.isRedirecting = false
    this.lastRefreshAttempt = 0
    this.startProactiveRefresh()
  }

  // Reset redirecting flag (call when navigation completes)
  resetRedirecting() {
    console.log("🔄 [ApiService] Resetting redirecting flag")
    this.isRedirecting = false
  }

  // Check if refresh has failed (to prevent unnecessary API calls)
  hasRefreshFailed(): boolean {
    return this.refreshFailed
  }

  // Check if refresh is currently in progress
  getIsRefreshing(): boolean {
    return this.isRefreshing
  }

  // Wait for token refresh to complete (if in progress)
  async waitForTokenRefresh(): Promise<void> {
    if (!this.isRefreshing) {
      return
    }

    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (!this.isRefreshing) {
          clearInterval(checkInterval)
          if (this.refreshFailed) {
            reject(new Error("Token refresh failed"))
          } else {
            resolve()
          }
        }
      }, 100)

      setTimeout(() => {
        clearInterval(checkInterval)
        reject(new Error("Token refresh timeout"))
      }, 10000)
    })
  }

  // Start proactive token refresh (refresh before expiration)
  startProactiveRefresh() {
    if (this.refreshInterval) {
      console.log("🔄 [ApiService] Clearing existing proactive refresh interval")
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }

    if (this.refreshFailed) {
      console.warn("⚠️ [ApiService] Cannot start proactive refresh - refresh already failed")
      return
    }

    const REFRESH_INTERVAL_MS = 14 * 60 * 1000 // 14 minutes

    console.log(
      `⏰ [ApiService] Starting proactive token refresh (every ${
        REFRESH_INTERVAL_MS / 1000 / 60
      } minutes)`
    )

    this.refreshInterval = setInterval(async () => {
      if (this.isRefreshing) {
        console.log("⏭️ [ApiService] Skipping proactive refresh - already refreshing")
        return
      }

      if (this.refreshFailed) {
        console.log("⏭️ [ApiService] Skipping proactive refresh - refresh permanently failed")
        if (this.refreshInterval) {
          clearInterval(this.refreshInterval)
          this.refreshInterval = null
          console.log("🛑 [ApiService] Stopped proactive refresh interval - permanent failure")
        }
        return
      }

      try {
        console.log("🔄 [ApiService] Proactive token refresh triggered (14min interval)")
        await axios.post(
          `${import.meta.env.VITE_API_URL || "http://localhost:3000"}/auth/refresh`,
          {},
          {
            withCredentials: true,
            headers: {
              "X-Application": "backoffice",
            },
          }
        )
        console.log("✅ [ApiService] Token refreshed proactively - notifying WebSocket")
        this.notifyWebSocketRefresh()
      } catch (error) {
        console.error("❌ [ApiService] Proactive token refresh failed:", error)
        // Don't stop interval on temporary errors
        console.log(
          "⏭️ [ApiService] Proactive refresh failed but interval continues - will retry next cycle"
        )
      }
    }, REFRESH_INTERVAL_MS)
  }

  // Stop proactive refresh (call on logout)
  stopProactiveRefresh() {
    if (this.refreshInterval) {
      console.log("🛑 [ApiService] Stopping proactive token refresh")
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
  }

  // Notify WebSocket service about token refresh
  private notifyWebSocketRefresh() {
    console.log("📡 [ApiService] Notifying WebSocket about token refresh")
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof globalThis !== "undefined" && "window" in globalThis) {
      const win = globalThis.window as any
      if (win && win.dispatchEvent && win.CustomEvent) {
        try {
          win.dispatchEvent(new win.CustomEvent("token-refreshed"))
          console.log("✅ [ApiService] Token refresh event dispatched to WebSocket")
        } catch (err) {
          console.error("❌ [ApiService] Failed to dispatch token refresh event:", err)
        }
      }
    }
  }

  async get<T>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url)
    return response.data
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: { headers?: Record<string, string>; timeout?: number }
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data, config)
    return response.data
  }

  async postRaw(
    url: string,
    data?: unknown,
    config?: { headers?: Record<string, string>; timeout?: number }
  ): Promise<AxiosResponse> {
    return this.api.post(url, data, config)
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data)
    return response.data
  }

  async delete<T>(url: string, config?: { data?: unknown }): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url, config)
    return response.data
  }
}

export const apiService = new ApiService()
